import { useTranslation } from 'react-i18next';
import {
  Button,
  Modal,
  Label,
  InputText,
  InputDate,
  InputSelect,
} from '@teo/components';
import { useState, useContext, useEffect } from 'react';
import ColorPicker from '@/components/ColorPicker';
import IconPicker from '@/components/IconPicker';
import { config } from '@/config';
import { Userwithorganisationwithidps } from '@/models';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { downloadLink, parseDate } from '@/util';
import i18n from 'i18next';
import { useGet } from '@/query/fetchHooks';
import { PaginationMultiSelect } from '@/components/PaginationMultiSelect';
import { NotificationContext } from '@/components/Contexts/NotificationContext';
import { axios, publicAxios } from '@/axios';
import { PaginationSingleSelect } from '@/components/PaginationSingleSelect';

function ReportModal({ onClose }: any) {
  const { t } = useTranslation();
  const { setShowNotification } = useContext(NotificationContext);

  const [name, setName] = useState('');

  const [from, setFrom] = useState<Date | undefined>();
  const [to, setTo] = useState<Date | undefined>(new Date());

  const [members, setMembers] = useState<any[]>([]);
  const [selectTeams, setSelectTeams] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);

  const [enableLessonSelect, setEnableLessonSelect] = useState(false);
  const [enableCourseSelect, setEnableCourseSelect] = useState(false);
  const [enableUserSelect, setEnableUserSelect] = useState(false);
  const [enableTeamSelect, setEnableTeamSelect] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState<any[]>([]);
  const [selectedLessons, setSelectedLessons] = useState<any[]>([]);
  const [enableStudentSelect, setEnableStudentSelect] = useState(false);

  const { auth } = useContext(AuthContext);
  const authUser = auth?.user as Userwithorganisationwithidps;

  const reportOptions = [
    {
      value: 'studentOverview',
      label: t('report.studentOverview'),
    },
    {
      value: 'courseOverview',
      label: t('pages.report.course_overview'),
    },
    {
      value: 'aggregateLessonCsv',
      label: t('report.resultsCsv'),
    },
    {
      value: 'aggregateLessonXlsx',
      label: t('report.resultsXlsx'),
    },
  ];

  const reportTeamList = [
    {
      value: 'studentOverview',
      label: t('report.studentOverview'),
    },
    {
      value: 'courseOverview',
      label: t('report.courseOverview'),
    },
    {
      value: 'courseCsv',
      label: t('report.courseCsv'),
    },
    {
      value: 'courseXlsx',
      label: t('report.courseXlsx'),
    },
    {
      value: 'coursedetailsCsv',
      label: t('report.coursedetailsCsv'),
    },
    {
      value: 'coursedetailsXlsx',
      label: t('report.coursedetailsXlsx'),
    },
    {
      value: 'usersCsv',
      label: t('report.usersCsv'),
    },
    {
      value: 'usersXlsx',
      label: t('report.usersXlsx'),
    },
    {
      value: 'aggregateLessonCsv',
      label: t('report.resultsCsv'),
    },
    {
      value: 'aggregateLessonXlsx',
      label: t('report.resultsXlsx'),
    },
    {
      value: 'exportResults',
      label: t('report.resultsPdf'),
    },
  ];

  useEffect(() => {
    setMembers([]);
  }, [enableUserSelect, enableStudentSelect]);

  const role = Math.max(
    authUser?.role || 0,
    authUser?.activeOrganisationRole || 0
  );

  const [reportType, setReportType] = useState(
    role <= 40 ? reportOptions[0] : reportTeamList[0]
  );

  const [myTeams, setMyTeams] = useState<any>(null);

  useEffect(() => {
    switch (reportType.value) {
      case 'aggregateLessonCsv':
      case 'aggregateLessonXlsx':
        setEnableLessonSelect(true);
        setEnableCourseSelect(true);
        setEnableUserSelect(false);
        setEnableStudentSelect(false);
        setEnableTeamSelect(false);
        break;
      case 'courseOverview':
      case 'coursedetailsCsv':
      case 'coursedetailsXlsx':
        setEnableLessonSelect(false);
        setEnableCourseSelect(true);
        setEnableUserSelect(false);
        setEnableStudentSelect(false);
        setEnableTeamSelect(false);
        break;
      case 'studentOverview':
        setEnableLessonSelect(false);
        setEnableCourseSelect(false);
        setEnableUserSelect(true);
        setEnableStudentSelect(false);
        setEnableTeamSelect(true);
        break;
      case 'exportResults':
        setEnableLessonSelect(false);
        setEnableCourseSelect(true);
        setEnableUserSelect(false);
        setEnableStudentSelect(true);
        setEnableTeamSelect(false);
        break;
      default:
        setEnableLessonSelect(false);
        setEnableCourseSelect(false);
        setEnableUserSelect(true);
        setEnableStudentSelect(false);
        setEnableTeamSelect(true);
        break;
    }
  }, [reportType]);

  useGet(`/myteams`, {
    enabled: !(role <= 40),
    userId: (authUser?.role || 0) >= 90 ? undefined : authUser?.id,
    organisationId: authUser?.activeOrganisationId,
    teamStatus: 'active',
    onSuccess: (results: any) => {
      setMyTeams(results?.data);
    },
  });

  const downloadReport = () => {
    const teamsId =
      selectTeams?.length > 0
        ? selectTeams?.map((team: any) => team.id)
        : myTeams?.map((team: any) => team.id);
    const promises = [];
    const link = `${config.teoreport}/report/?lang=${i18n.language}`;
    const userIds = members?.map((member: any) => member.id || member.userId);
    promises.push(
      axios.post(link, {
        email: authUser.email,
        organisationId: authUser.activeOrganisationId,
        download: true,
        lang: i18n.language,
        teamIds: enableTeamSelect ? teamsId : undefined,
        courseIds: enableCourseSelect
          ? selectedCourses.map((x) => x.courseId)
          : undefined,
        lessonIds: enableLessonSelect
          ? selectedLessons.map((x) => x.lessonId)
          : undefined,
        userIds: userIds,
        endDate: null,
        startDate: null,
        formType: reportType?.value,
      })
    );

    Promise.allSettled(promises).then((results: any) => {
      results[0]?.value?.data?.link &&
        downloadLink(results[0]?.value?.data?.link);
    });

    setShowNotification({
      message: t('pages.report_page.report_download_message'),
      delay: 5000,
    });
    // .then((result: any) => {
    //   setShowNotification({
    //     message: t('pages.report_page.report_download_message'),
    //     delay: 5000,
    //   });
    // });
    onClose();
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();

        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [onClose]);

  return (
    <div className="bg-white">
      <Modal isOpen={true}>
        <Modal.Header onClose={onClose}>
          <h2
            className="text-sm font-semibold"
            data-testid="addCategoriesHeader"
          >
            {t('pages.report.title')}
          </h2>
        </Modal.Header>

        <Modal.Body>
          <h1 className="text-base font-semibold">
            {t('pages.report.subtitle')}
          </h1>
          <p className="mt-2 text-sm font-normal text-[#7d7d81]">
            {t('pages.report.description')}
          </p>

          <form
            className="w-full max-w-[440px] flex-col max-md:h-full max-md:overflow-hidden"
            data-testid="addCategoriesForm"
          >
            <div>
              <Label htmlFor="courseLang">
                <p className="mt-4 text-sm font-semibold">
                  {t('pages.report.type_select')}
                </p>
              </Label>
              <div id="courseLang">
                <InputSelect<any>
                  onChange={(newReportType) => {
                    setReportType(newReportType);
                  }}
                  value={reportType}
                  options={role <= 40 ? reportOptions : reportTeamList}
                >
                  {(options) => <span>{options?.label}</span>}
                </InputSelect>
              </div>
            </div>

            {enableCourseSelect && (
              <div>
                <Label htmlFor="members">
                  <p className="mt-4 text-sm font-semibold">
                    {t(`pages.report_page.course`)}
                  </p>
                </Label>
                <PaginationSingleSelect
                  value={selectedCourses?.[0]}
                  onChange={(value: any) => {
                    setSelectedLessons([]);
                    setSelectedCourses([value]);
                  }}
                  keyFn={(row: any) => row?.courseId}
                  fetch={useGet.bind(
                    null,
                    `/organisationcoursedetails?organisationId=${auth?.user?.activeOrganisationId}`
                  )}
                  displayValue={(el: any) => {
                    if (!el || el?.length === 0) return '';
                    return (
                      ((el?.courseName ? el?.courseName : el?.name) || '') +
                      ` (${parseDate(el?.createdAt)})`
                    );
                  }}
                  defaultSort={[{ id: 'courseName', desc: false }]}
                  render={(row: any) => {
                    return (
                      <div className="flex justify-between">
                        <span>
                          {row?.courseName ? row?.courseName : row?.name}
                        </span>
                        <span>{`${parseDate(row?.createdAt)}`}</span>
                      </div>
                    );
                  }}
                />
              </div>
            )}

            {enableLessonSelect && selectedCourses.length > 0 && (
              <div className="z-[1] mb-1 mt-4 text-sm font-semibold text-black">
                <p className="mb-1">
                  {t(`pages.create_library.lesson`)} ({t('forms.optional')})
                </p>
                <div className="flex">
                  <span className="w-full">
                    <PaginationSingleSelect
                      id="teams-report"
                      value={selectedLessons?.[0]}
                      onChange={(value: any) => {
                        setSelectedLessons([value]);
                      }}
                      keyFn={(row: any) => row?.id}
                      fetch={useGet.bind(
                        null,
                        `/usedcourselessonassets?organisationId=${auth?.user?.activeOrganisationId}&coursecontentId=${selectedCourses[0].coursecontentId}&courseId=${selectedCourses[0].id}`
                      )}
                      defaultSort={[{ id: 'name', desc: false }]}
                      render={(row: any) => {
                        return row?.courseName ? row?.courseName : row?.name;
                      }}
                    />
                  </span>
                </div>
              </div>
            )}

            {enableUserSelect ||
              (enableStudentSelect && selectedCourses.length > 0 && (
                <>
                  <div>
                    <Label htmlFor="members">
                      <p className="mt-4 text-sm font-semibold">
                        {t('pages.report.select_users')} ({t('forms.optional')})
                      </p>
                    </Label>
                    <PaginationMultiSelect
                      id="members"
                      value={members}
                      onChange={(value: any) => {
                        setMembers(value);
                      }}
                      keyFn={(row: any) => row?.id}
                      fetch={useGet.bind(
                        null,
                        enableStudentSelect
                          ? `/students?organisationId=${auth?.user?.activeOrganisationId}&courseId=${selectedCourses?.[0].id}`
                          : `/memberdetails?lang=${i18n.language}&organisationId=${auth?.user?.activeOrganisationId}`
                      )}
                      defaultSort={[{ id: 'username', desc: false }]}
                      render={(row: any) => {
                        return row?.username;
                      }}
                    />
                  </div>
                  {(reportType?.value === 'detailed' ||
                    reportType?.value === 'overview') && (
                    <div>
                      <Label htmlFor="courses">
                        <p className="mt-4 text-sm font-semibold">
                          {t('pages.report.select_courses')} (
                          {t('forms.optional')})
                        </p>
                      </Label>
                      <PaginationMultiSelect
                        id="courses"
                        value={courses}
                        onChange={(value: any) => {
                          setCourses(value);
                        }}
                        displayValue={(el: any) => {
                          if (!el || el?.length === 0) return '';
                          return (
                            ((el?.courseName ? el?.courseName : el?.name) ||
                              '') + ` (${parseDate(el?.createdAt)})`
                          );
                        }}
                        keyFn={(row: any) => row?.id}
                        fetch={useGet.bind(
                          null,
                          `/mycourses?organisationId=${auth?.user?.activeOrganisationId}&userId=${authUser?.id}`
                        )}
                        defaultSort={[{ id: 'courseName', desc: false }]}
                        render={(row: any) => {
                          return (
                            <div className="flex justify-between">
                              <span>
                                {row?.courseName ? row?.courseName : row?.name}
                              </span>
                              <span>{`${parseDate(row?.createdAt)}`}</span>
                            </div>
                          );
                        }}
                      />
                    </div>
                  )}
                </>
              ))}

            {!(role <= 40) && myTeams && enableTeamSelect && (
              <div>
                <Label htmlFor="members">
                  <p className="mt-4 text-sm font-semibold">
                    {t('pages.report.select_teams')} ({t('forms.optional')})
                  </p>
                </Label>
                <PaginationMultiSelect
                  id="teams"
                  value={selectTeams}
                  onChange={(value: any) => {
                    setSelectTeams(value);
                  }}
                  keyFn={(row: any) => row?.id}
                  fetch={useGet.bind(
                    null,
                    `/myteams?userId=${authUser?.id}&organisationId=${auth?.user?.activeOrganisationId}&teamStatus=active`
                  )}
                  defaultSort={[{ id: 'teamName', desc: false }]}
                  render={(row: any) => {
                    return row?.teamName;
                  }}
                />
              </div>
            )}
          </form>
        </Modal.Body>

        <Modal.Footer
          cancelButtonLabel={t('pages.manage_categories.cancel_button')}
          onCancel={onClose}
        >
          <div data-testid="addCategoriesFooter">
            <Button
              type="button"
              disabled={!(role <= 40) && !myTeams}
              onClick={() => downloadReport()}
            >
              {t('pages.manage_categories.download')}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ReportModal;
